import React, { useState } from "react";
import MarketingNav from "../../components/MarketingNav";
import Footer from "../../components/Footer";
import LivePlayground from "../../components/LivePlayground";
import AnimatedCounter from "../../components/AnimatedCounter";
import SEO from "../../components/SEO";
import { Link } from "react-router-dom";
import {
  Shield, Zap, Lock, Sparkles, ArrowRight, Check, Radar, ScrollText,
  Terminal, Activity, Layers, GitBranch, FileCheck2, Fingerprint,
  Cpu, Clock, ShieldCheck, Building2, Gauge, Scale, Globe2,
} from "lucide-react";

const trust = [
  "SOC 2 Type II", "HIPAA BAA", "EU AI Act Art. 14", "NIST AI RMF", "FedRAMP Moderate", "ISO 42001",
];

const planes = [
  {
    key: "gateway",
    icon: Zap,
    meta: "Data Plane",
    title: "Gateway",
    body: "Horizontally scaled inline proxy in Go. +6ms p95 over direct provider calls. The only component on the hot path — memory-safe, stateless, region-local.",
    stats: [
      { k: "p95 latency", v: "3.8 ms" },
      { k: "Deployment", v: "K8s / Nitro" },
    ],
    tone: "dark",
  },
  {
    key: "policy",
    icon: ScrollText,
    meta: "Control Plane",
    title: "Policy Plane",
    body: "Versioned, content-addressable policy store. Author in SentryDSL, compile to bytecode, distribute to Gateway pods in under 15 seconds. Every version SHA-256 signed.",
    stats: [
      { k: "Rollout", v: "<15 s" },
      { k: "Integrity", v: "SHA-256" },
    ],
    tone: "light",
  },
  {
    key: "evidence",
    icon: Lock,
    meta: "Control Plane",
    title: "Evidence Plane",
    body: "Append-only audit ledger on ClickHouse + S3. Every decision, prompt, response, and policy bundle — queryable by auditors, exportable to Snowflake and Databricks.",
    stats: [
      { k: "Retention", v: "7 yrs" },
      { k: "Backends", v: "CH + S3" },
    ],
    tone: "light",
  },
];

const compliance = [
  { icon: Scale,        name: "EU AI Act",       sub: "Article 14 human-oversight logging, high-risk categorization, incident reporting.", status: "Ready" },
  { icon: FileCheck2,   name: "NIST AI RMF",     sub: "Govern, Map, Measure, Manage — control mappings shipped in evidence bundles.",       status: "Ready" },
  { icon: ShieldCheck,  name: "SOC 2 Type II",   sub: "Security, Availability, Confidentiality. Continuous evidence collection.",            status: "Audited" },
  { icon: Fingerprint,  name: "HIPAA BAA",       sub: "PHI redaction pre-provider, at-rest encryption, signed access ledger.",               status: "Signed" },
  { icon: Building2,    name: "FedRAMP",         sub: "Moderate baseline. High baseline via Citadel + Enclave with hardware attestation.",   status: "Roadmap" },
  { icon: Globe2,       name: "ISO/IEC 42001",   sub: "AI management system controls mapped to Sentry policy families 1:1.",                 status: "Aligned" },
];

const differentiators = [
  { icon: Terminal, title: "SentryDSL", body: "Regex-first policy language. Version-controlled, compilable, testable in the browser." },
  { icon: Activity, title: "Live Decisions",  body: "Every prompt, redaction, deny — streamed to Observatory with per-request causal trace." },
  { icon: GitBranch, title: "Reproducible",  body: "Any decision replayable against any policy version. Regulators verify the artifact." },
  { icon: Gauge,     title: "Hot-path fast",  body: "Sub-4ms p95 policy evaluation. Never the bottleneck for a real-time LLM app." },
];

export default function Home() {
  const [feed] = useState(() => genFeed());

  return (
    <div>
      <SEO
        path="/"
        title="The Governance OS for Regulated AI"
        description="Sentry is the inline AI governance platform. Enforce policies on every prompt in under 4ms, redact PII, block prompt injection, and ship signed audit bundles regulators can verify."
        keywords="AI governance platform, LLM observability, prompt firewall, EU AI Act, NIST AI RMF, SOC 2 AI, HIPAA LLM compliance, prompt injection prevention, PII redaction, inline AI proxy"
      />
      <MarketingNav />

      {/* ══════════════════════ HERO ══════════════════════ */}
      <section className="relative overflow-hidden bg-cream">
        <div className="absolute inset-0 hero-grid pointer-events-none" />
        <div className="absolute -top-40 -right-40 w-[720px] h-[720px] hero-sun rounded-full pointer-events-none" />
        <div className="absolute top-1/3 -left-24 w-[420px] h-[420px] hero-navy-halo rounded-full pointer-events-none" />

        <div className="container-x relative pt-14 pb-24">
          {/* live-status pill */}
          <div className="rise flex justify-center mb-8">
            <div data-testid="hero-status-pill" className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-[rgba(10,14,26,0.08)] shadow-sm">
              <span className="pulse-dot" />
              <span className="text-[12px] font-semibold text-navy tracking-wide">
                <AnimatedCounter to={18412903} live liveStep={900} liveDelta={7} formatter={(n)=>Math.floor(n).toLocaleString()} /> requests screened this hour
              </span>
              <span className="text-[11px] ink border-l border-[rgba(10,14,26,0.1)] pl-2 ml-1">v1.0 GA</span>
            </div>
          </div>

          {/* eyebrow */}
          <div className="rise text-center mb-4">
            <span className="chip chip-orange"><Sparkles size={12} /> The Governance OS for Regulated AI</span>
          </div>

          {/* headline */}
          <h1 className="rise rise-delay-1 font-display text-center font-bold text-navy leading-[1.02] mx-auto max-w-5xl text-4xl sm:text-5xl lg:text-[68px]">
            Deploy, monitor, and <span className="text-orange">prove the safety</span><br className="hidden sm:block" /> of every AI in production.
          </h1>

          <p className="rise rise-delay-2 ink mt-6 text-center max-w-2xl mx-auto text-lg">
            Sentry stands in front of every model call — screening prompts, enforcing policy,
            and writing an immutable audit trail regulators can verify.
          </p>

          {/* CTAs */}
          <div className="rise rise-delay-3 mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" data-testid="hero-book-demo" className="btn-primary">Book a demo <ArrowRight size={16} /></Link>
            <Link to="/app" data-testid="hero-see-dashboard" className="btn-ghost">See the live dashboard</Link>
          </div>

          {/* trust chips */}
          <div className="rise rise-delay-4 mt-8 flex flex-wrap justify-center gap-2 max-w-3xl mx-auto">
            {trust.map(t => (
              <span key={t} data-testid={`trust-${t.split(' ')[0].toLowerCase()}`} className="chip chip-navy">
                <Check size={12} /> {t}
              </span>
            ))}
          </div>

          {/* hero surface: split — left playground, right terminal */}
          <div className="mt-16 grid lg:grid-cols-12 gap-6 rise rise-delay-4">
            <div className="lg:col-span-7">
              <LivePlayground />
            </div>
            <div className="lg:col-span-5">
              <HeroTerminal feed={feed} />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════ LIVE METRICS ══════════════════════ */}
      <section className="container-x py-24">
        <div className="max-w-2xl mb-14 reveal">
          <div className="eyebrow text-orange">Live from production</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-navy">Every decision. In real time. On the record.</h2>
          <p className="ink mt-4 max-w-xl">Aggregate metrics streamed from the Sentry Evidence Plane across all customer tenants. Refreshed every second.</p>
        </div>

        <div className="grid md:grid-cols-4 gap-4">
          <MetricCard
            icon={Activity}
            k="Requests screened / 24h"
            valueEl={<AnimatedCounter to={18412903} formatter={(n)=>Math.floor(n).toLocaleString()} live liveStep={950} liveDelta={9} />}
            sub="across all tenants"
            trend="+ 12.4% vs 24h prior"
            fill={0.86}
            tone="orange"
          />
          <MetricCard
            icon={Shield}
            k="Denied / redacted"
            valueEl={<AnimatedCounter to={41287} formatter={(n)=>Math.floor(n).toLocaleString()} live liveStep={1400} liveDelta={1} />}
            sub="policy-blocked at gateway"
            trend="0.22% overall block rate"
            fill={0.32}
            tone="navy"
          />
          <MetricCard
            icon={Clock}
            k="Gateway p95 latency"
            valueEl={<AnimatedCounter to={3.82} decimals={2} suffix=" ms" />}
            sub="policy evaluation"
            trend="under 4ms SLO"
            fill={0.19}
            tone="navy"
          />
          <MetricCard
            icon={Lock}
            k="Signed audit bundles"
            valueEl={<AnimatedCounter to={5842} formatter={(n)=>Math.floor(n).toLocaleString()} live liveStep={2400} liveDelta={1} />}
            sub="last 30 days"
            trend="7-yr retention"
            fill={0.62}
            tone="orange"
          />
        </div>

        {/* small differentiator row */}
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {differentiators.map((d, i) => (
            <div key={d.title} className={`rounded-2xl p-5 bg-white border border-[rgba(10,14,26,0.08)] lift rise rise-delay-${(i%4)+1}`}>
              <div className="w-9 h-9 rounded-lg bg-[rgba(255,107,53,0.12)] flex items-center justify-center">
                <d.icon size={18} className="text-orange" />
              </div>
              <div className="font-display font-bold text-navy mt-3">{d.title}</div>
              <div className="ink text-sm mt-1">{d.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════ ARCHITECTURE (3 PLANES) ══════════════════════ */}
      <section className="bg-white border-y border-[rgba(10,14,26,0.06)]">
        <div className="container-x py-24">
          <div className="grid lg:grid-cols-12 items-end gap-8 mb-14">
            <div className="lg:col-span-7 reveal">
              <div className="eyebrow text-orange">The Sentry Architecture</div>
              <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-navy">
                Three planes. <span className="accent-underline">One guarantee.</span>
              </h2>
              <p className="ink mt-4 max-w-2xl">
                Data plane isolated, control plane shared. If Evidence goes down, Gateway keeps serving traffic.
                If Policy goes down, Gateway serves on the last-known-good bundle. There is no single point at which
                observability breaks enforcement.
              </p>
            </div>
            <div className="lg:col-span-5 reveal">
              <div className="grid grid-cols-3 gap-3">
                <MiniStat label="Availability" value="99.99%" />
                <MiniStat label="Regions" value="4 · US/EU" />
                <MiniStat label="SDKs" value="Node · Py · Go" />
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {planes.map((p, i) => (
              <div
                key={p.title}
                data-testid={`arch-plane-${p.key}`}
                className={`${p.tone === 'dark' ? 'arch-card arch-card--dark' : 'arch-card'} lift p-7 rise rise-delay-${i+1}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${p.tone === 'dark' ? 'bg-orange/20' : 'bg-[rgba(255,107,53,0.12)]'}`}>
                  <p.icon size={22} className="text-orange" />
                </div>
                <div className={`eyebrow mt-4 ${p.tone === 'dark' ? 'text-star' : 'ink'}`}>{p.meta}</div>
                <h3 className={`font-display text-xl font-bold mt-1 ${p.tone === 'dark' ? 'text-cream' : 'text-navy'}`}>{p.title}</h3>
                <p className={`mt-3 text-sm leading-relaxed ${p.tone === 'dark' ? 'text-cream/70' : 'ink'}`}>{p.body}</p>

                <div className={`mt-5 grid grid-cols-2 gap-2 pt-4 border-t ${p.tone === 'dark' ? 'border-white/10' : 'border-[rgba(10,14,26,0.08)]'}`}>
                  {p.stats.map(s => (
                    <div key={s.k}>
                      <div className={`text-[10px] uppercase tracking-widest font-semibold ${p.tone === 'dark' ? 'text-cream/50' : 'text-navy/50'}`}>{s.k}</div>
                      <div className={`font-mono text-sm mt-0.5 ${p.tone === 'dark' ? 'text-star' : 'text-navy'}`}>{s.v}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* connecting flow */}
          <div className="mt-10 rounded-2xl border border-[rgba(10,14,26,0.08)] p-6 bg-[#FAF7F2]">
            <div className="flex items-center gap-3 flex-wrap text-sm font-mono">
              <span className="chip chip-navy">client</span>
              <ArrowRight size={14} className="text-navy/40" />
              <span className="chip" style={{background:'rgba(255,107,53,0.14)', color:'#D14F1F'}}>Gateway · evaluate</span>
              <ArrowRight size={14} className="text-navy/40" />
              <span className="chip chip-navy">Policy Bundle vN</span>
              <ArrowRight size={14} className="text-navy/40" />
              <span className="chip" style={{background:'rgba(34,197,94,0.14)', color:'#166534'}}>Provider (OpenAI / Anthropic / Google)</span>
              <ArrowRight size={14} className="text-navy/40" />
              <span className="chip chip-navy">Evidence Plane</span>
              <ArrowRight size={14} className="text-navy/40" />
              <span className="chip chip-gold">Signed Bundle · SHA-256</span>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════ DARK OBSERVATORY PREVIEW ══════════════════════ */}
      <section className="starfield text-cream">
        <div className="container-x py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <span className="chip chip-cream mb-6"><Radar size={12} /> The Night Watch</span>
              <h2 className="font-display text-3xl md:text-4xl font-bold">Observatory. Every AI decision, made queryable.</h2>
              <p className="text-cream/70 mt-5">
                Observatory ingests every decision from Sentry's Evidence Plane and builds a Causal Trace Graph —
                prompt, context, retrieval, tools, response, user, and business action, all connected.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-cream/80">
                {[
                  "Decision search + 5 prebuilt dashboards",
                  "Causal Trace Graph (CTG) with root-cause inference",
                  "AI Seer — LLM-powered root cause & remediation",
                  "Drift detection: embedding + behavioral",
                  "Slack, PagerDuty, Datadog, Splunk export",
                ].map(x => (
                  <li key={x} className="flex gap-2"><Check size={16} className="text-[#FFD98A] mt-0.5"/> {x}</li>
                ))}
              </ul>
              <div className="mt-8 flex gap-3">
                <Link to="/observatory" className="btn-primary text-sm">Explore Observatory <ArrowRight size={14} /></Link>
                <Link to="/pricing" className="btn-ghost-dark text-sm">Compare tiers</Link>
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-6 backdrop-blur">
                <div className="grid grid-cols-4 gap-4 text-center">
                  {[
                    { k: "REQUESTS", v: "18.4M", sub: "last 24h" },
                    { k: "DENIED", v: "412", sub: "policy-blocked" },
                    { k: "P95 LATENCY", v: "3.8ms", sub: "gateway" },
                    { k: "SPEND", v: "$12,847", sub: "provider cost" },
                  ].map(m => (
                    <div key={m.k} className="border border-white/10 rounded-xl p-4">
                      <div className="eyebrow text-[#FFD98A]/80" style={{fontSize:10}}>{m.k}</div>
                      <div className="font-display text-2xl font-bold mt-1">{m.v}</div>
                      <div className="text-[11px] text-cream/50 mt-1">{m.sub}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 font-mono text-xs bg-black/30 rounded-lg p-4 border border-white/5">
                  <div className="text-cream/50"># POST /api/v1/gateway/evaluate</div>
                  <div className="text-[#FFD98A]">{'{'} "prompt": "Email me at jane@example.com", "model": "gpt-4" {'}'}</div>
                  <div className="mt-2 text-cream/50"># response</div>
                  <div className="text-orange">action: <span className="text-white">redact</span></div>
                  <div className="text-orange">policy: <span className="text-white">PII: Email Redaction</span></div>
                  <div className="text-orange">latency_ms: <span className="text-white">2.14</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════ COMPLIANCE ══════════════════════ */}
      <section className="container-x py-24">
        <div className="max-w-2xl mb-14 reveal">
          <div className="eyebrow text-orange">Compliance is the moat</div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mt-3 text-navy">Built for the frameworks auditors already trust.</h2>
          <p className="ink mt-4">Every deploy ships with mapped controls, signed evidence, and reproducible policy versions. No spreadsheet gymnastics.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {compliance.map((c, i) => (
            <div key={c.name} data-testid={`compliance-${c.name.split(' ')[0].toLowerCase()}`} className={`rounded-2xl bg-white border border-[rgba(10,14,26,0.08)] p-6 lift reveal`} style={{animationDelay: `${i*70}ms`}}>
              <div className="flex items-start justify-between">
                <div className="badge-shield">
                  <c.icon size={22} className="text-white" />
                </div>
                <span className={`chip ${c.status === 'Ready' || c.status === 'Audited' || c.status === 'Signed' || c.status === 'Aligned' ? 'chip-gold' : 'chip-navy'}`}>{c.status}</span>
              </div>
              <div className="font-display text-lg font-bold text-navy mt-4">{c.name}</div>
              <div className="ink text-sm mt-2 leading-relaxed">{c.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════ FINAL CTA ══════════════════════ */}
      <section className="container-x pb-24">
        <div className="rounded-3xl p-12 bg-navy text-cream relative overflow-hidden">
          <div className="absolute inset-0 starfield-soft opacity-70 pointer-events-none" />
          <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full" style={{background:'radial-gradient(circle, rgba(255,107,53,0.35) 0%, transparent 60%)', filter:'blur(20px)'}} />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-bold">Turn AI compliance into a shipped artifact.</h3>
              <p className="text-cream/70 mt-3 max-w-lg">Signed audit bundles. Reproducible policies. Answers regulators can verify.</p>
              <div className="mt-6 flex items-center gap-4 text-sm text-cream/60">
                <span className="inline-flex items-center gap-1"><span className="pulse-dot-orange" /> Live gateway · never a mock</span>
              </div>
            </div>
            <div className="flex md:justify-end gap-3">
              <Link to="/contact" className="btn-primary">Book a demo</Link>
              <Link to="/pricing" className="btn-ghost-dark">See pricing</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

/* ─────────────────── helpers ─────────────────── */

function MetricCard({ icon: Icon, k, valueEl, sub, trend, fill = 0.5, tone = "orange" }) {
  const barColor = tone === "orange" ? "#FF6B35" : "#0A0E1A";
  return (
    <div data-testid={`metric-${k.replace(/[^a-z]/gi,'-').toLowerCase()}`} className="rounded-2xl p-6 bg-white border border-[rgba(10,14,26,0.08)] lift">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-lg bg-[rgba(255,107,53,0.12)] flex items-center justify-center">
          <Icon size={18} className="text-orange" />
        </div>
        <span className="pulse-dot" />
      </div>
      <div className="eyebrow ink mt-4" style={{fontSize:10}}>{k}</div>
      <div className="font-display text-3xl font-bold text-navy mt-1 num-in">{valueEl}</div>
      <div className="text-xs ink mt-1">{sub}</div>

      <div className="mt-4 h-1.5 rounded-full bg-[rgba(10,14,26,0.06)] overflow-hidden">
        <div
          className="bar-meter-fill h-full rounded-full"
          style={{ background: barColor, width: '100%', ['--w']: fill }}
        />
      </div>
      <div className="text-[11px] font-semibold text-orange mt-2">{trend}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-[rgba(10,14,26,0.08)] p-3 bg-white">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-navy/50">{label}</div>
      <div className="font-display font-bold text-navy mt-1 text-sm">{value}</div>
    </div>
  );
}

function HeroTerminal({ feed }) {
  return (
    <div data-testid="hero-terminal" className="term p-4 h-full">
      <div className="flex items-center gap-2 pb-3 mb-3 border-b border-white/5">
        <span className="term-dot" style={{ background: '#ef4444' }} />
        <span className="term-dot" style={{ background: '#f59e0b' }} />
        <span className="term-dot" style={{ background: '#22c55e' }} />
        <span className="ml-2 text-[11px] text-cream/50">sentry · live decision feed</span>
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-cream/60">
          <span className="pulse-dot" /> streaming
        </span>
      </div>

      <div className="space-y-1.5 max-h-[280px] overflow-hidden">
        {feed.map((f, i) => (
          <div key={i} className="flex items-start gap-2 text-[12px]">
            <span className="text-cream/40 shrink-0">{f.time}</span>
            <span className={`shrink-0 font-bold ${
              f.action === 'ALLOW' ? 'text-emerald-400'
              : f.action === 'DENY' ? 'text-red-400'
              : f.action === 'REDACT' ? 'text-amber-400'
              : 'text-orange'
            }`}>{f.action.padEnd(6, ' ')}</span>
            <span className="text-cream/80 truncate">{f.text}</span>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-white/5 text-[11px] text-cream/50 font-mono">
        <span className="text-orange">$</span> sentry tail --tenant demo <span className="cursor-blink">▍</span>
      </div>
    </div>
  );
}

function genFeed() {
  const templates = [
    { action: "ALLOW",  text: "Draft marketing email · gpt-4o · 3.2ms" },
    { action: "REDACT", text: "PII: Email Redaction · redacted 1 span · 2.8ms" },
    { action: "DENY",   text: "Prompt Injection Guard · pattern match" },
    { action: "ALLOW",  text: "Summarize Q4 earnings · claude-sonnet-4.5 · 4.1ms" },
    { action: "FLAG",   text: "Toxicity Flagging · escalated to review" },
    { action: "DENY",   text: "PII: US SSN · pattern match · alerted" },
    { action: "ALLOW",  text: "Recommend recipe · gemini-3-flash · 1.9ms" },
    { action: "REDACT", text: "PII: Email Redaction · redacted 2 spans · 3.1ms" },
    { action: "ALLOW",  text: "Convert 32C→F · gpt-4o-mini · 1.4ms" },
    { action: "DENY",   text: "Jailbreak: DAN · pattern match · alerted" },
    { action: "ALLOW",  text: "Explain photosynthesis · gemini-3-flash · 2.2ms" },
    { action: "FLAG",   text: "Article 14 · high-risk domain · logged" },
  ];
  const now = Date.now();
  return templates.map((t, i) => ({
    ...t,
    time: new Date(now - i * 4300).toISOString().slice(11, 19),
  }));
}
